import type { Vec2 } from "../types";

/**
 * Renderer-agnostic path commands for a part outline. Both the Konva
 * `sceneFunc` and the SVG preview consume the same command list so the two
 * renderers can never disagree about a shape.
 */
export type PathCmd =
  | { t: "M"; x: number; y: number }
  | { t: "L"; x: number; y: number }
  | { t: "Q"; cx: number; cy: number; x: number; y: number }
  | { t: "Z" };

/**
 * Build a closed path for `outline`. When `cornerRadius > 0` every vertex is
 * rounded with a quadratic curve whose tangent points sit `r` along each
 * adjacent edge (clamped to half the edge so tiny shapes don't invert).
 */
export function outlinePath(outline: Vec2[], cornerRadius = 0): PathCmd[] {
  const n = outline.length;
  if (n === 0) return [];
  if (n < 3 || cornerRadius <= 0) {
    const cmds: PathCmd[] = [{ t: "M", x: outline[0].x, y: outline[0].y }];
    for (let i = 1; i < n; i++) cmds.push({ t: "L", x: outline[i].x, y: outline[i].y });
    cmds.push({ t: "Z" });
    return cmds;
  }
  const cmds: PathCmd[] = [];
  for (let i = 0; i < n; i++) {
    const prev = outline[(i - 1 + n) % n];
    const cur = outline[i];
    const next = outline[(i + 1) % n];
    const d1 = Math.hypot(cur.x - prev.x, cur.y - prev.y);
    const d2 = Math.hypot(next.x - cur.x, next.y - cur.y);
    const r = Math.min(cornerRadius, d1 / 2, d2 / 2);
    if (r <= 1e-6 || d1 === 0 || d2 === 0) {
      if (i === 0) cmds.push({ t: "M", x: cur.x, y: cur.y });
      else cmds.push({ t: "L", x: cur.x, y: cur.y });
      continue;
    }
    const inX = cur.x - ((cur.x - prev.x) / d1) * r;
    const inY = cur.y - ((cur.y - prev.y) / d1) * r;
    const outX = cur.x + ((next.x - cur.x) / d2) * r;
    const outY = cur.y + ((next.y - cur.y) / d2) * r;
    if (i === 0) cmds.push({ t: "M", x: inX, y: inY });
    else cmds.push({ t: "L", x: inX, y: inY });
    cmds.push({ t: "Q", cx: cur.x, cy: cur.y, x: outX, y: outY });
  }
  cmds.push({ t: "Z" });
  return cmds;
}

/** Serialize commands to an SVG `d` attribute, optionally scaling/offsetting. */
export function toSvgPath(cmds: PathCmd[], scale = 1, ox = 0, oy = 0): string {
  const f = (v: number) => (Math.round(v * 100) / 100).toString();
  let out = "";
  for (const c of cmds) {
    switch (c.t) {
      case "M":
        out += `M${f(c.x * scale + ox)} ${f(c.y * scale + oy)}`;
        break;
      case "L":
        out += `L${f(c.x * scale + ox)} ${f(c.y * scale + oy)}`;
        break;
      case "Q":
        out += `Q${f(c.cx * scale + ox)} ${f(c.cy * scale + oy)} ${f(c.x * scale + ox)} ${f(c.y * scale + oy)}`;
        break;
      case "Z":
        out += "Z";
        break;
    }
  }
  return out;
}

/** Minimal 2D context surface we need — satisfied by both CanvasRenderingContext2D and Konva.Context. */
export interface PathContext {
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number): void;
  closePath(): void;
}

/** Replay commands onto a canvas-like context (does not fill/stroke). */
export function tracePath(ctx: PathContext, cmds: PathCmd[]) {
  ctx.beginPath();
  for (const c of cmds) {
    switch (c.t) {
      case "M":
        ctx.moveTo(c.x, c.y);
        break;
      case "L":
        ctx.lineTo(c.x, c.y);
        break;
      case "Q":
        ctx.quadraticCurveTo(c.cx, c.cy, c.x, c.y);
        break;
      case "Z":
        ctx.closePath();
        break;
    }
  }
}
